import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Loader2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  DollarSign
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesManager = ({ user }) => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedService, setExpandedService] = useState(null);

  const categories = [
    'Consultations',
    'Astrology Readings',
    'Spiritual Guidance'
  ];

  const iconOptions = [
    { value: 'heart', label: 'Heart (Relationships)' },
    { value: 'activity', label: 'Activity (Health)' },
    { value: 'sparkles', label: 'Sparkles (Prosperity)' },
    { value: 'home', label: 'Home (Harmony)' },
    { value: 'building', label: 'Building (Construction)' },
    { value: 'mappin', label: 'Map Pin (Relocation)' },
    { value: 'calendar', label: 'Calendar (Date Selection)' },
    { value: 'refresh', label: 'Refresh (Updates)' },
    { value: 'users', label: 'Users (Couples/Teams)' },
    { value: 'filetext', label: 'Document (Written Reading)' },
    { value: 'briefcase', label: 'Briefcase (Corporate)' },
    { value: 'star', label: 'Star (Premium)' },
    { value: 'compass', label: 'Compass (Guidance)' }
  ];

  const emptyService = {
    name: '',
    slug: '',
    category: 'Consultations',
    starting_price: 0,
    description: '',
    duration: '',
    image: '',
    icon: 'sparkles',
    includes: [],
    pricing_tiers: [],
    faq: [],
    active: true,
    order: 0
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/consultation-services`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setServices(data.services || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (service) => {
    setSaving(true);
    try {
      const url = service.id
        ? `${BACKEND_URL}/api/admin/consultation-services/${service.id}`
        : `${BACKEND_URL}/api/admin/consultation-services`;
      
      const method = service.id ? 'PUT' : 'POST';
      
      // Generate slug if not provided
      if (!service.slug) {
        service.slug = service.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(service)
      });

      if (response.ok) {
        await fetchServices();
        setEditingService(null);
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Error saving service:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/consultation-services/${serviceId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        await fetchServices();
      }
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  const ServiceForm = ({ service, onSave, onCancel }) => {
    const [form, setForm] = useState(service);
    const [includeInput, setIncludeInput] = useState('');

    const addInclude = () => {
      if (includeInput.trim()) {
        setForm(prev => ({
          ...prev,
          includes: [...(prev.includes || []), includeInput.trim()]
        }));
        setIncludeInput('');
      }
    };

    const removeInclude = (idx) => {
      setForm(prev => ({
        ...prev,
        includes: prev.includes.filter((_, i) => i !== idx)
      }));
    };

    const addPricingTier = () => {
      setForm(prev => ({
        ...prev,
        pricing_tiers: [...(prev.pricing_tiers || []), { name: '', price: 0, description: '', features: [], popular: false }]
      }));
    };

    const updatePricingTier = (idx, field, value) => {
      setForm(prev => ({
        ...prev,
        pricing_tiers: prev.pricing_tiers.map((tier, i) => 
          i === idx ? { ...tier, [field]: value } : tier
        )
      }));
    };

    const removePricingTier = (idx) => {
      setForm(prev => ({
        ...prev,
        pricing_tiers: prev.pricing_tiers.filter((_, i) => i !== idx)
      }));
    };

    const addFaq = () => {
      setForm(prev => ({
        ...prev,
        faq: [...(prev.faq || []), { question: '', answer: '' }]
      }));
    };

    const updateFaq = (idx, field, value) => {
      setForm(prev => ({
        ...prev,
        faq: prev.faq.map((item, i) => 
          i === idx ? { ...item, [field]: value } : item
        )
      }));
    };

    const removeFaq = (idx) => {
      setForm(prev => ({
        ...prev,
        faq: prev.faq.filter((_, i) => i !== idx)
      }));
    };

    return (
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">
            {service.id ? 'Edit Service' : 'New Service'}
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300 mb-2 block">Service Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="e.g., Relationship Feng Shui"
              />
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Category *</Label>
              <select
                value={form.category}
                onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-gray-300 mb-2 block">Starting Price ($) *</Label>
              <Input
                type="number"
                value={form.starting_price}
                onChange={(e) => setForm(prev => ({ ...prev, starting_price: parseFloat(e.target.value) || 0 }))}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Duration</Label>
              <Input
                value={form.duration}
                onChange={(e) => setForm(prev => ({ ...prev, duration: e.target.value }))}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="e.g., 60-90 minutes"
              />
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Icon</Label>
              <select
                value={form.icon}
                onChange={(e) => setForm(prev => ({ ...prev, icon: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2"
              >
                {iconOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-gray-300 mb-2 block">Image URL</Label>
            <Input
              value={form.image}
              onChange={(e) => setForm(prev => ({ ...prev, image: e.target.value }))}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <div>
            <Label className="text-gray-300 mb-2 block">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              className="bg-gray-800 border-gray-700 text-white min-h-[100px]"
              placeholder="Describe this consultation service..."
            />
          </div>

          {/* What's Included */}
          <div>
            <Label className="text-gray-300 mb-2 block">What's Included</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={includeInput}
                onChange={(e) => setIncludeInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInclude())}
                className="bg-gray-800 border-gray-700 text-white flex-1"
                placeholder="Add an included item..."
              />
              <Button type="button" onClick={addInclude} variant="outline" className="border-gray-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {(form.includes || []).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between bg-gray-800/50 px-3 py-2 rounded-lg">
                  <span className="text-gray-300">{item}</span>
                  <button onClick={() => removeInclude(idx)} className="text-gray-500 hover:text-red-400">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Tiers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-gray-300">Pricing Tiers (Optional)</Label>
              <Button type="button" onClick={addPricingTier} variant="outline" size="sm" className="border-gray-700">
                <Plus className="w-4 h-4 mr-1" /> Add Tier
              </Button>
            </div>
            <div className="space-y-4">
              {(form.pricing_tiers || []).map((tier, idx) => (
                <div key={idx} className="bg-gray-800/50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-medium">Tier {idx + 1}</span>
                    <button onClick={() => removePricingTier(idx)} className="text-gray-500 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <Input
                      value={tier.name}
                      onChange={(e) => updatePricingTier(idx, 'name', e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Tier name"
                    />
                    <Input
                      type="number"
                      value={tier.price}
                      onChange={(e) => updatePricingTier(idx, 'price', parseFloat(e.target.value) || 0)}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Price"
                    />
                  </div>
                  <Input
                    value={tier.description}
                    onChange={(e) => updatePricingTier(idx, 'description', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white mb-2"
                    placeholder="Tier description"
                  />
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={tier.popular}
                      onCheckedChange={(checked) => updatePricingTier(idx, 'popular', checked)}
                    />
                    <span className="text-gray-400 text-sm">Mark as popular</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-gray-300">FAQ (Optional)</Label>
              <Button type="button" onClick={addFaq} variant="outline" size="sm" className="border-gray-700">
                <Plus className="w-4 h-4 mr-1" /> Add FAQ
              </Button>
            </div>
            <div className="space-y-4">
              {(form.faq || []).map((item, idx) => (
                <div key={idx} className="bg-gray-800/50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-medium">FAQ {idx + 1}</span>
                    <button onClick={() => removeFaq(idx)} className="text-gray-500 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <Input
                    value={item.question}
                    onChange={(e) => updateFaq(idx, 'question', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white mb-2"
                    placeholder="Question"
                  />
                  <Textarea
                    value={item.answer}
                    onChange={(e) => updateFaq(idx, 'answer', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Answer"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <Switch
              checked={form.active}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, active: checked }))}
            />
            <span className="text-gray-300">Service is active and visible</span>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
            <Button type="button" onClick={onCancel} variant="outline" className="border-gray-700">
              Cancel
            </Button>
            <Button
              onClick={() => onSave(form)}
              disabled={saving || !form.name}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Service
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Consultation Services</h1>
              <p className="text-gray-400 text-sm">Manage your consultation fees and services</p>
            </div>
          </div>
          {!isCreating && !editingService && (
            <Button
              onClick={() => { setIsCreating(true); setEditingService(emptyService); }}
              className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          )}
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingService) && (
          <ServiceForm
            service={editingService || emptyService}
            onSave={handleSave}
            onCancel={() => { setIsCreating(false); setEditingService(null); }}
          />
        )}

        {/* Services List by Category */}
        {!isCreating && !editingService && (
          <div className="space-y-8">
            {categories.map(category => {
              const categoryServices = services.filter(s => s.category === category);
              if (categoryServices.length === 0) return null;

              return (
                <div key={category}>
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-amber-500" />
                    {category}
                    <span className="text-gray-500 font-normal text-sm">({categoryServices.length})</span>
                  </h2>
                  <div className="space-y-3">
                    {categoryServices.map(service => (
                      <div
                        key={service.id}
                        className="bg-gray-900/50 border border-gray-800 rounded-lg overflow-hidden"
                      >
                        <div className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-4">
                            <GripVertical className="w-5 h-5 text-gray-600" />
                            {service.image ? (
                              <img
                                src={service.image}
                                alt={service.name}
                                className="w-16 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-16 h-12 bg-gray-800 rounded flex items-center justify-center">
                                <ImageIcon className="w-6 h-6 text-gray-600" />
                              </div>
                            )}
                            <div>
                              <h3 className="text-white font-medium">{service.name}</h3>
                              <p className="text-amber-400 text-sm">Starting at ${service.starting_price}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded ${service.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-700 text-gray-400'}`}>
                              {service.active ? 'Active' : 'Inactive'}
                            </span>
                            <button
                              onClick={() => setExpandedService(expandedService === service.id ? null : service.id)}
                              className="p-2 text-gray-400 hover:text-white"
                            >
                              {expandedService === service.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => setEditingService(service)}
                              className="p-2 text-gray-400 hover:text-amber-400"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(service.id)}
                              className="p-2 text-gray-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Expanded Details */}
                        {expandedService === service.id && (
                          <div className="px-4 pb-4 border-t border-gray-800 pt-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">Duration:</span>
                                <p className="text-white">{service.duration || 'Not specified'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Pricing Tiers:</span>
                                <p className="text-white">{service.pricing_tiers?.length || 0}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Includes:</span>
                                <p className="text-white">{service.includes?.length || 0} items</p>
                              </div>
                              <div>
                                <span className="text-gray-500">FAQ:</span>
                                <p className="text-white">{service.faq?.length || 0} questions</p>
                              </div>
                            </div>
                            {service.description && (
                              <p className="text-gray-400 mt-3 text-sm">{service.description}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {services.length === 0 && (
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-white font-medium mb-2">No services yet</h3>
                <p className="text-gray-500 mb-4">Add your first consultation service to get started.</p>
                <Button
                  onClick={() => { setIsCreating(true); setEditingService(emptyService); }}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Service
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesManager;
